/**
 * Everything the Genograma y Ecomapa dock entry opens.
 *
 * Lives here rather than in either dock, so the home surface and a patient's
 * page behave identically — and so neither dock has to carry map data for a
 * therapist who has not adopted the tool.
 *
 * The one difference between the two is who the map is about. On a patient's
 * page that is already known; on the home surface it is asked once, with the
 * same picker Agendar and Pagos use.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Modal } from "../../../../shared/ui/modal";
import PatientFinder, {
    type PatientOption,
} from "../../../../shared/ui/patient/PatientFinder";
import { useSystemicMapSubjects } from "../../application/useSystemicMapSubjects";
import { mapTitle } from "../../domain/title";
import { createSystemicMap } from "../../application/systemicMaps";
import type { MapKind } from "../../domain/types";
import MapKindChooser from "./MapKindChooser";
import MapListModal from "./MapListModal";
import SystemicMapLaunchSheet from "./SystemicMapLaunchSheet";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Fixed on a patient or couple page; absent on the home surface. */
    subject?: { kind: "patient" | "couple" | "group"; id: string; label: string } | null;
    /**
     * Opens the board in a new tab rather than navigating.
     *
     * Set where leaving would cost something: from inside a session, going to
     * the board would take the therapist out of the call.
     */
    openInNewTab?: boolean;
    /**
     * Hands the map — made or chosen — to the caller instead of going to it.
     * Set inside a call, which opens the board on its own stage for the
     * therapist and the patient at once.
     */
    onOpenMap?: (mapId: string) => void;
}

export default function SystemicMapDockActions({
    isOpen,
    onClose,
    subject = null,
    openInNewTab = false,
    onOpenMap,
}: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [pickingSubject, setPickingSubject] = useState(false);
    const [chosenSubject, setChosenSubject] = useState<PatientOption | null>(null);
    const [choosingKind, setChoosingKind] = useState(false);
    // An open board belongs to nobody's record. Offered from the home surface
    // only: on a patient's page there is already a subject, and a board about
    // "nobody" reached from their page would be a trap.
    const [open, setOpen] = useState(false);
    const [listOpen, setListOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Only fetched when there is nobody in context.
    const subjects = useSystemicMapSubjects(!subject && isOpen);

    const target = subject
        ? { id: subject.id, name: subject.label, kind: subject.kind }
        : chosenSubject;

    const startCreate = () => {
        onClose();
        setOpen(false);
        if (subject) {
            setChoosingKind(true);
            return;
        }
        setPickingSubject(true);
    };

    /** A board for a room, not for a record: everyone joins by the same link. */
    const startOpenBoard = () => {
        onClose();
        setChosenSubject(null);
        setOpen(true);
        setChoosingKind(true);
    };

    const create = async (kind: MapKind) => {
        if (!open && !target) return;
        setError(null);
        try {
            const isCouple = !open && target?.kind === "couple";
            const isGroup = !open && target?.kind === "group";
            const data = await createSystemicMap({
                kind,
                title: open
                    ? t("systemicMap.dock.openBoardTitle")
                    : mapTitle(t(`systemicMap.kind.${kind}`), target?.name),
                patientId: open || isCouple || isGroup ? undefined : target?.id,
                couplePatientId: isCouple ? target?.id : undefined,
                groupPatientId: isGroup ? target?.id : undefined,
            });
            setChoosingKind(false);
            setChosenSubject(null);
            setOpen(false);
            if (onOpenMap) {
                onOpenMap(data.id);
                return;
            }
            const path = `/systemic-map/${data.id}`;
            if (openInNewTab) {
                window.open(path, "_blank", "noopener,noreferrer");
            } else {
                navigate(path);
            }
        } catch {
            setError(t("systemicMap.meeting.error"));
        }
    };

    return (
        <>
            <SystemicMapLaunchSheet
                isOpen={isOpen}
                onClose={onClose}
                onCreate={startCreate}
                onOpenBoard={subject ? null : startOpenBoard}
                onManage={() => {
                    onClose();
                    setListOpen(true);
                }}
                subjectLabel={subject?.label ?? null}
            />

            <Modal
                isOpen={pickingSubject}
                onClose={() => setPickingSubject(false)}
                ariaLabel={t("systemicMap.dock.choosePatient")}
                className="w-full max-w-md rounded-2xl bg-surface-overlay p-6"
            >
                <h2 className="text-lg font-semibold text-content-primary">
                    {t("systemicMap.dock.choosePatient")}
                </h2>
                <div className="mt-4">
                    <PatientFinder
                        patients={subjects.options}
                        selectedId={chosenSubject?.id ?? null}
                        onSelect={(id) => {
                            const found =
                                subjects.options.find((option) => option.id === id) ?? null;
                            setChosenSubject(found);
                            if (found) {
                                setPickingSubject(false);
                                setChoosingKind(true);
                            }
                        }}
                    />
                </div>
            </Modal>

            <MapKindChooser
                isOpen={choosingKind}
                onClose={() => setChoosingKind(false)}
                onChoose={create}
                subjectLabel={open ? t("systemicMap.dock.openBoard") : target?.name}
                error={error}
            />

            <MapListModal
                isOpen={listOpen}
                onClose={() => setListOpen(false)}
                patientId={subject?.kind === "patient" ? subject.id : undefined}
                couplePatientId={subject?.kind === "couple" ? subject.id : undefined}
                groupPatientId={subject?.kind === "group" ? subject.id : undefined}
                openInNewTab={openInNewTab}
                onOpenMap={
                    onOpenMap
                        ? (mapId) => {
                              setListOpen(false);
                              onOpenMap(mapId);
                          }
                        : undefined
                }
                onCreate={() => {
                    setListOpen(false);
                    startCreate();
                }}
            />
        </>
    );
}
